import {
  DEFAULT_QRA_VERSION,
  QRA_VERSIONS,
  QRA_VERSION_LABELS,
  isQraVersion,
  type QraVersion,
} from '~~/shared/chat/qraVersion'
import { readPref, writePref } from '~/composables/usePersistedPref'

const STORAGE_KEY = 'marcus-qra-version'

/** Client-only, once per page load. See the identical flag in `useModelSelector` for why module scope is safe. */
let restored = false

/**
 * SUPERADMIN-only QRA version toggle. Mirrors `useModelSelector`'s shape (shared `useState`,
 * localStorage-persisted, restored after hydration). `options` is empty for non-superadmins, so the
 * selector renders nothing and every request defaults to `2.0` — the server re-gates on superadmin
 * regardless.
 */
export function useQraVersion() {
  const { profile } = useUserProfile()

  const isSuperAdmin = computed(() => !!profile.value?.is_superadmin)

  const options = computed(() =>
    isSuperAdmin.value
      ? QRA_VERSIONS.map(id => ({ id, label: QRA_VERSION_LABELS[id] }))
      : [],
  )

  // Server-side initializer — it cannot see `localStorage`, and hydration reuses its payload value
  // rather than re-running it, so the stored version is restored below instead (AlexAmin/marcus#228).
  const qraVersion = useState<QraVersion>('qra-version', () => DEFAULT_QRA_VERSION)

  if (import.meta.client && !restored) {
    restored = true
    nextTick(() => {
      const stored = readPref(STORAGE_KEY)
      if (isQraVersion(stored)) qraVersion.value = stored
    })
  }

  function setQraVersion(id: QraVersion) {
    qraVersion.value = id
    if (import.meta.client) writePref(STORAGE_KEY, id)
  }

  return { qraVersion, setQraVersion, options, isSuperAdmin }
}
